import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import FilterSelect from "../components/FilterSelect";
import SEO from "../components/SEO";
import { useTranslation } from "react-i18next";

const TYPE_LABELS = {
    place: "Adresse",
    walk: "Balade",
    experience: "Expérience",
};

const TYPE_LABELS_EN = {
    place: "Place",
    walk: "Walk",
    experience: "Experience",
};

export default function Galerie() {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || "fr";
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hovered, setHovered] = useState(null);

    const city = params.get("city") || "";
    const type = params.get("type") || "";

    useEffect(() => {
        api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        api.get("/gallery", { params: { city: city || undefined, lang } })
            .then((r) => setItems(r.data))
            .finally(() => setLoading(false));
    }, [city, lang]);

    const filtered = useMemo(() => {
        let arr = items;
        if (type) arr = arr.filter((it) => it.type === type);
        return arr;
    }, [items, type]);

    const update = (key, value) => {
        const next = new URLSearchParams(params);
        if (!value) next.delete(key);
        else next.set(key, value);
        setParams(next);
    };

    const cityName = (slug) => {
        const c = cities.find((x) => x.slug === slug);
        return c?.name || slug?.replace(/-/g, " ");
    };

    const labels = lang === "en" ? TYPE_LABELS_EN : TYPE_LABELS;

    return (
        <div className="pt-28" data-testid="galerie-page">
            <SEO
                title={lang === "en"
                    ? "Photo Gallery — every place, every walk, every experience"
                    : "Galerie photo — toutes les adresses, balades et expériences"}
                description={lang === "en"
                    ? "A photographic wall of every authentic place, walk and experience between Le Tréport, Mers-les-Bains, Eu and Aumale. 700+ images, all real, no stock."
                    : "Un mur photo de toutes les adresses, balades et expériences vérifiées entre Le Tréport, Mers-les-Bains, Eu et Aumale. 700+ images authentiques, zéro stock."}
                path="/galerie"
            />

            <section className="px-6 md:px-12 max-w-[1600px] mx-auto pt-16 md:pt-24 pb-16">
                <p className="cp-tiny text-cp-foret mb-8">
                    {lang === "en" ? "Chapter VII · Gallery" : "Chapitre VII · Galerie"}
                </p>
                <h1 className="cp-serif text-4xl sm:text-5xl md:text-[56px] lg:text-[72px] xl:text-[92px] leading-[1.02] text-cp-encre max-w-5xl tracking-[-0.025em]">
                    {lang === "en" ? (
                        <>Seven hundred<br /><em className="cp-display-italic text-cp-foret">images</em>, no stock.</>
                    ) : (
                        <>Sept cents <em className="cp-display-italic text-cp-foret">images</em>,<br />zéro fiction.</>
                    )}
                </h1>
                <p className="cp-serif italic text-xl md:text-2xl font-light text-cp-gris leading-relaxed mt-12 max-w-3xl">
                    {lang === "en"
                        ? "Every photo on this site comes from the place itself — owner-uploaded, audited by Vision AI, never stock. Click any image to step inside."
                        : "Chaque photo de ce site vient du lieu lui-même — déposée par les propriétaires, vérifiée par notre IA visuelle, jamais générique. Cliquez pour entrer."}
                </p>
            </section>

            {/* FILTERS */}
            <section className="px-6 md:px-12 max-w-[1600px] mx-auto py-5 border-y border-cp-border mb-12 sticky top-20 md:top-24 z-30 bg-cp-ivoire/95 backdrop-blur-xl" data-testid="galerie-filters">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 items-end">
                    <FilterSelect
                        label={lang === "en" ? "City" : "Ville"}
                        value={city}
                        onChange={(v) => update("city", v)}
                        testId="filter-city"
                        options={[
                            { value: "", label: lang === "en" ? "All cities" : "Toutes les villes" },
                            ...cities.map((c) => ({ value: c.slug, label: c.name })),
                        ]}
                    />
                    <FilterSelect
                        label={lang === "en" ? "Type" : "Type"}
                        value={type}
                        onChange={(v) => update("type", v)}
                        testId="filter-type"
                        options={[
                            { value: "", label: lang === "en" ? "Everything" : "Tout" },
                            { value: "place", label: lang === "en" ? "Places" : "Adresses" },
                            { value: "walk", label: lang === "en" ? "Walks" : "Balades" },
                            { value: "experience", label: lang === "en" ? "Experiences" : "Expériences" },
                        ]}
                    />
                </div>
                <div className="flex items-center justify-between mt-3">
                    <p className="cp-tiny text-cp-gris">
                        {filtered.length} {lang === "en" ? "images" : "images"}
                        {(city || type) && (
                            <button onClick={() => { ["city", "type"].forEach(k => update(k, "")); }} className="ml-3 text-cp-foret hover:underline cp-serif italic" data-testid="filter-reset">
                                {lang === "en" ? "Clear all ×" : "Tout effacer ×"}
                            </button>
                        )}
                    </p>
                </div>
            </section>

            {/* WALL */}
            <section className="px-2 md:px-6 max-w-[1800px] mx-auto pb-32" data-testid="galerie-wall">
                {loading ? (
                    <p className="text-cp-gris px-4">…</p>
                ) : filtered.length === 0 ? (
                    <p className="text-cp-gris italic text-lg px-4">
                        {lang === "en" ? "Nothing matches these filters." : "Aucune image ne correspond à ces filtres."}
                    </p>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1 md:gap-2">
                        {filtered.map((it, i) => (
                            <Link
                                key={`${it.type}-${it.slug}`}
                                to={it.url}
                                onMouseEnter={() => setHovered(`${it.type}-${it.slug}`)}
                                onMouseLeave={() => setHovered(null)}
                                className="group relative block overflow-hidden bg-cp-encre/5 aspect-[4/5]"
                                data-testid={`gallery-tile-${it.type}-${it.slug}`}
                                style={{ animationDelay: `${Math.min(i * 8, 600)}ms` }}
                            >
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}${it.image}`}
                                    alt={it.name}
                                    loading={i < 20 ? "eager" : "lazy"}
                                    className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-[1.04] group-hover:brightness-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-cp-encre/85 via-cp-encre/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                                    <p className="cp-tiny text-cp-ivoire/70 mb-1">
                                        {labels[it.type]} · {cityName(it.city_slug)}
                                    </p>
                                    <p className="cp-serif italic text-cp-ivoire text-base md:text-lg leading-snug line-clamp-2">
                                        {it.name}
                                    </p>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
